import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';

// Start Auth
import Login from './components/auth/Login';
import UserType from './components/auth/UserType';
import SignuUpStudent from './components/auth/SignuUpStudent';
import SignuUpTeachers from './components/auth/SignuUpTeachers';
import ForgotPassword from './components/auth/Forgot';

// Start Main
import Header from './components/main/Header';
import Footer from './components/main/Footer';

// Start Pages
import Home from "./components/pages/Home";
import CommingSoon from './components/pages/CommingSoon';
import CoursesPortal from './components/pages/CoursesPortal';
import Exam from './components/pages/Exam';
import ReviewAnswers from './components/pages/ReviewAnswers';
import NotFound from './components/pages/NotFound';

// Start User Pages
import UserProfile from './components/admin/UserProfile';

// Start Hooks
import Top from './components/Top';
import ScrollToTop from './components/ScrollToTop';
import UseScrollAnimation from './Hooks/UseScrollAnimation';

const App = () => {
    const location = useLocation();
    const [isNotFound, setIsNotFound] = useState(false);

    const authRoutes = ['/login', '/signup', '/user-type', '/forgot', '/comming-soon'];
    const isAuthRoute = authRoutes.some(route => location.pathname.includes(route));

    // Define the routes to be redirected to '/comming-soon'
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const redirectRoutes = ['/about', '/services', '/educational-level', '/teachers', '/contact'];

    // Check if the current route is for the Exam page or ReviewAnswers page
    const isExamPage = location.pathname.split('/').length === 5;  // The exam page path will have exactly 5 segments
    const isReviewAnswersPage = location.pathname.includes('/review-answers'); // Check if the path includes '/review-answers'

    // Use useEffect to determine if the current route leads to NotFound
    useEffect(() => {
        const definedRoutes = [
            '/', '/login', '/user-type', '/signup/student', '/signup/teachers', '/forgot',
            '/about', '/services', '/educational-level', '/teachers', '/contact', '/comming-soon',
            '/profile'
        ];

        const isDynamicRoute = location.pathname.startsWith('/courses-portal/') ||
            location.pathname.split('/').length === 5 ||
            location.pathname.includes('/review-answers');

        // Check if the route should redirect to '/comming-soon'
        if (redirectRoutes.includes(location.pathname)) {
            setIsNotFound(false); // Reset isNotFound to false since we're redirecting
            return;
        }

        // If the route is not in definedRoutes and not a dynamic route, it's a NotFound page
        if (!definedRoutes.includes(location.pathname) && !isDynamicRoute) {
            setIsNotFound(true);
        } else {
            setIsNotFound(false);
        }
    }, [location.pathname, redirectRoutes]); // أضفت redirectRoutes إلى مصفوفة التبعيات

    // Perform the redirect to '/comming-soon' after useEffect
    if (redirectRoutes.includes(location.pathname)) {
        return <Navigate to="/comming-soon" />;
    }

    return (
        <>
            {/* Render Header and Top only for non-auth, non-exam, non-review-answer, and non-not-found pages */}
            {!isAuthRoute && !isExamPage && !isReviewAnswersPage && !isNotFound && <Header />}
            {!isAuthRoute && !isExamPage && !isReviewAnswersPage && !isNotFound && <Top />}

            <Routes>
                {/* Auth Pages */}
                <Route path='/login' element={<Login />} />
                <Route path='/user-type' element={<UserType />} />
                <Route path='/signup/student' element={<SignuUpStudent />} />
                <Route path='/signup/teachers' element={<SignuUpTeachers />} />
                <Route path='/forgot' element={<ForgotPassword />} />

                {/* Main Pages */}
                <Route path='/' element={<Home />} />
                <Route path='/about' element={<Navigate to="/comming-soon" />} />
                <Route path='/services' element={<Navigate to="/comming-soon" />} />
                <Route path='/educational-level' element={<Navigate to="/comming-soon" />} />
                <Route path='/teachers' element={<Navigate to="/comming-soon" />} />
                <Route path='/contact' element={<Navigate to="/comming-soon" />} />
                <Route path='/comming-soon' element={<CommingSoon />} />
                <Route path='/courses-portal/:courseName/:coursesClassTitle' element={<CoursesPortal />} />
                <Route path="/:courseName/:coursesClassTitle/:unitName/:lessonName" element={<Exam />} />
                <Route path="/:courseName/:coursesClassTitle/:unitName/:lessonName/review-answers" element={<ReviewAnswers />} />
                <Route path='*' element={<NotFound />} />

                {/* User Pages */}
                <Route path="/profile" element={<UserProfile />} />
            </Routes>

            {/* Only render Footer and ScrollToTop for non-auth, non-exam, non-review-answer, and non-not-found routes */}
            {!isAuthRoute && !isExamPage && !isReviewAnswersPage && !isNotFound && <Footer />}
            <ScrollToTop />
            <UseScrollAnimation />
        </>
    );
};

export default App;